import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ListResponse, ProfileCategoryCountsResponse } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

import type {
  AiSearchResult,
  ListProfilesPage,
} from '../infrastructure/discovered-lp-detail.gateway';
import { type FacetOptions, initialState } from './discovered-lp-detail.state';

/** Payload for `loadSucceeded`: the list and its comment count. */
export interface LoadedDetail {
  list: ListResponse;
  commentCount: number;
}

/**
 * Client Discovered LP Detail slice. Reducers are pure state transitions only;
 * every side effect (the list load, the paginated profile search, Ask AI, the
 * facet sources) lives in the saga, which dispatches these as it progresses.
 */
const slice = createSlice({
  name: 'discoveredLpDetail',
  initialState,
  reducers: {
    // ── The list ──
    listIdSet: (state, action: PayloadAction<string>) => {
      state.listId = action.payload;
    },
    // `loadStarted` keeps the existing `list` so a reload does not blank the
    // page (the page guards on `list`, not on `loadStatus`).
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<LoadedDetail>) => {
      state.list = action.payload.list;
      state.commentCount = action.payload.commentCount;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    /** A comment was posted from the Comments panel: keep the tile's count in step. */
    commentAdded: (state) => {
      state.commentCount += 1;
    },

    // ── Cursor + search (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },
    filtersSet: (state, action: PayloadAction<FilterGroup | null>) => {
      state.filters = action.payload;
      state.page = 0;
    },
    searchSet: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0;
    },

    // ── Profiles ──
    rowsStarted: (state) => {
      state.rowsLoading = true;
      state.rowsError = null;
    },
    rowsSucceeded: (state, action: PayloadAction<ListProfilesPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.rowsLoading = false;
    },
    rowsFailed: (state, action: PayloadAction<string>) => {
      state.rowsLoading = false;
      state.rowsError = action.payload;
    },

    // ── Ask AI ──
    aiSearchStarted: (state) => {
      state.aiSearch.status = 'asking';
      state.aiSearch.error = null;
    },
    aiSearchAnswered: (state, action: PayloadAction<AiSearchResult & { query: string }>) => {
      state.aiSearch.status = 'idle';
      state.aiSearch.answer = action.payload;
      state.aiSearch.answerId += 1;
    },
    aiSearchFailed: (state, action: PayloadAction<string>) => {
      state.aiSearch.status = 'failed';
      state.aiSearch.error = action.payload;
    },
    /** The interpretation line was closed, or its rules undone. */
    aiSearchDismissed: (state) => {
      state.aiSearch.status = 'idle';
      state.aiSearch.error = null;
      state.aiSearch.answer = null;
    },

    // ── Facet sources ──
    categoryCountsLoaded: (state, action: PayloadAction<ProfileCategoryCountsResponse>) => {
      state.categoryCounts = action.payload;
    },
    /** A failed fetch clears the figures rather than freezing stale ones. */
    categoryCountsCleared: (state) => {
      state.categoryCounts = null;
    },
    facetOptionsLoaded: (state, action: PayloadAction<FacetOptions>) => {
      state.facetOptions = action.payload;
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const discoveredLpDetailActions = slice.actions;
export const discoveredLpDetailReducer = slice.reducer;
